import { useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  ArrowLeftRight,
  Bell,
  CircleHelp,
  FileText,
  LayoutDashboard,
  LifeBuoy,
  LogOut,
  Menu,
  Plus,
  Search,
  Send,
  UserRound,
  Wallet,
  X,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { Brand } from "./Brand";
import { ConfirmDialog } from "./ConfirmDialog";
import { useSession } from "../lib/session";
import { useWallet } from "../lib/queries";
import { initials, tzs } from "../lib/format";

const NAV = [
  { to: "/", label: "Overview", icon: LayoutDashboard, end: true },
  { to: "/sell", label: "Sell airtime", icon: Send },
  { to: "/wallet", label: "Wallet", icon: Wallet },
  { to: "/transactions", label: "Transactions", icon: ArrowLeftRight },
  { to: "/statements", label: "Statements", icon: FileText },
  { to: "/reports", label: "Reports", icon: FileText },
];

const SECONDARY = [
  { to: "/support", label: "Support", icon: LifeBuoy },
  { to: "/help", label: "Help centre", icon: CircleHelp },
  { to: "/profile", label: "Profile", icon: UserRound },
];

const MOBILE_NAV = [
  { to: "/", label: "Home", icon: LayoutDashboard, end: true },
  { to: "/sell", label: "Sell", icon: Send },
  { to: "/wallet", label: "Wallet", icon: Wallet },
  { to: "/transactions", label: "Activity", icon: ArrowLeftRight },
  { to: "/profile", label: "Profile", icon: UserRound },
];

export function AppShell() {
  const { user, agent, signOut } = useSession();
  const wallet = useWallet();
  const [open, setOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const title = [...NAV, ...SECONDARY].find((n) => n.to === location.pathname)?.label ?? "Overview";

  const handleSignOut = () => {
    signOut();
    navigate("/login");
  };

  return (
    <div className="shell">
      <aside className={`sidebar ${open ? "sidebar-open" : ""} ${sidebarCollapsed && !open ? "sidebar-collapsed" : ""}`} aria-label="Main navigation">
        <div className="sidebar-head">
          {!sidebarCollapsed || open ? <Brand /> : null}
          <button className="ev-icon-btn sidebar-close" onClick={() => setOpen(false)} aria-label="Close menu">
            <X />
          </button>
          <button className="ev-icon-btn sidebar-toggle" onClick={() => setSidebarCollapsed((p) => !p)} aria-label={sidebarCollapsed ? "Expand menu" : "Collapse menu"} style={{ display: "none" }}>
            {sidebarCollapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
          </button>
        </div>

        <nav className="nav">
          <span className="nav-label">{sidebarCollapsed && !open ? "..." : "Workspace"}</span>
          {NAV.map(({ to, label, icon: Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) => `nav-item ${isActive ? "nav-item-active" : ""}`}
              onClick={() => setOpen(false)}
              title={sidebarCollapsed && !open ? label : undefined}
            >
              <Icon size={18} strokeWidth={1.9} aria-hidden />
              <span>{label}</span>
            </NavLink>
          ))}

          <span className="nav-label nav-label-gap">{sidebarCollapsed && !open ? "" : "Account"}</span>
          {SECONDARY.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) => `nav-item ${isActive ? "nav-item-active" : ""}`}
              onClick={() => setOpen(false)}
              title={sidebarCollapsed && !open ? label : undefined}
            >
              <Icon size={18} strokeWidth={1.9} aria-hidden />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="sidebar-foot">
          {!sidebarCollapsed || open ? (
            <>
              <div className="sidebar-help">
                <span className="sidebar-help-badge">Need a hand?</span>
                <p>Talk to your evocha account manager on WhatsApp.</p>
                <a className="ev-btn ev-btn-sm ev-btn-secondary" href="https://wa.me/255700000000" target="_blank" rel="noreferrer">
                  Chat with support
                </a>
              </div>
              <div className="sidebar-user">
                <span className="ev-avatar">{initials(`${user?.first_name} ${user?.last_name}`)}</span>
                <div className="sidebar-user-text">
                  <strong>{user?.first_name} {user?.last_name}</strong>
                  <span>{agent?.code}</span>
                </div>
                <button
                  className="ev-icon-btn"
                  aria-label="Sign out"
                  onClick={() => setShowLogoutConfirm(true)}
                >
                  <LogOut size={17} />
                </button>
              </div>
            </>
          ) : (
            <div className="sidebar-user sidebar-user-collapsed">
              <button
                className="ev-icon-btn"
                aria-label="Sign out"
                onClick={() => setShowLogoutConfirm(true)}
                title="Sign out"
              >
                <LogOut size={17} />
              </button>
            </div>
          )}
        </div>
      </aside>

      {open && <button className="sidebar-scrim" aria-label="Close menu" onClick={() => setOpen(false)} />}

      <div className="main">
        <header className="topbar">
          <div className="topbar-left">
            <button className="ev-icon-btn topbar-menu" onClick={() => setOpen(true)} aria-label="Open menu">
              <Menu />
            </button>
            <div>
              <span className="topbar-eyebrow">Agent portal</span>
              <h1 className="topbar-title">{title}</h1>
            </div>
          </div>

          <div className="topbar-right">
            <button
              className="live-balance"
              onClick={() => navigate("/wallet")}
              title="Live wallet balance — refreshes automatically"
            >
              <Wallet size={15} />
              <span className="live-balance-value">
                {wallet.isPending ? "—" : tzs(wallet.data?.available_minor ?? 0)}
              </span>
              <span
                className={`live-balance-dot${wallet.isFetching ? " live-balance-pulse" : ""}`}
                aria-hidden
              />
            </button>
            <button className="ev-icon-btn topbar-collapse" onClick={() => setSidebarCollapsed((p) => !p)} aria-label="Toggle sidebar" style={{ display: "none" }}>
              {sidebarCollapsed ? <ChevronRight size={17} /> : <ChevronLeft size={17} />}
            </button>
            <button className="search-trigger" onClick={() => navigate("/transactions")}>
              <Search size={16} aria-hidden />
              <span>Search transactions…</span>
              <kbd>⌘K</kbd>
            </button>
            <button className="ev-icon-btn" aria-label="Notifications">
              <Bell />
              <span className="notif-dot" />
            </button>
            <NavLink to="/profile" className="ev-avatar ev-avatar-lg" aria-label="Profile">
              {initials(`${user?.first_name} ${user?.last_name}`)}
            </NavLink>
          </div>
        </header>

        <div className="page" key={location.pathname}>
          <div className="page-quick">
            <NavLink to="/sell" className="ev-btn ev-btn-sm ev-btn-primary">
              <Plus size={15} /> New sale
            </NavLink>
            <NavLink to="/wallet" className="ev-btn ev-btn-sm ev-btn-secondary">
              <Wallet size={15} /> Top up
            </NavLink>
          </div>
          <Outlet />
        </div>
      </div>

      <nav className="bottom-nav" aria-label="Quick navigation">
        {MOBILE_NAV.map(({ to, label, icon: Icon, end }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            className={({ isActive }) => `bottom-nav-item ${isActive ? "bottom-nav-item-active" : ""}`}
          >
            <Icon size={20} strokeWidth={1.9} aria-hidden />
            <span>{label}</span>
          </NavLink>
        ))}
      </nav>

      <ConfirmDialog
        open={showLogoutConfirm}
        onClose={() => setShowLogoutConfirm(false)}
        onConfirm={handleSignOut}
        title="Sign out?"
        message="Are you sure you want to sign out of the agent portal?"
        confirmLabel="Sign out"
        cancelLabel="Cancel"
      />
    </div>
  );
}
